import { MutableRefObject, ReactNode, useRef } from 'react';

import Button from '@components/button/Button';
import Icon from '@components/icon/Icon';
import testId from '@constants/testId';
import useOnClickOutside from '@utils/hooks';

interface ModalProps {
  onCancel?: () => void;
  isOpen: boolean;
  className?: string;
  children: ReactNode;
  isCloseIconRequired?: boolean;
  closeIconClassName?: string;
  isClickOutsideRequired?: boolean;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

const Modal = ({
  onCancel,
  isOpen,
  className = '',
  children,
  isCloseIconRequired = false,
  closeIconClassName = '',
  isClickOutsideRequired = true,
  ...props
}: ModalProps) => {
  const modalRef = useRef() as MutableRefObject<HTMLDivElement>;

  useOnClickOutside(modalRef, (e) => {
    if (isClickOutsideRequired && onCancel) {
      e.stopPropagation();
      onCancel();
    }
  });

  return (
    <div
      className={`fixed left-0 top-0 z-[60] flex size-full items-center justify-center transition-opacity duration-300 ease-in-out ${
        isOpen ? 'opacity-100' : 'hidden opacity-0'
      }`}
      {...props}
    >
      {/* Modal Overlay */}
      <div className="absolute size-full bg-black opacity-20" />

      {/* Modal Main Content */}
      <div
        className={`relative mx-auto max-h-[98%] overflow-y-auto rounded-lg bg-grey20 ${className}`}
        ref={modalRef}
      >
        {children}
        {isCloseIconRequired && (
          <Button
            variant="transparent"
            handleClick={onCancel}
            className={`absolute right-0 top-4 mr-2 ${closeIconClassName}`}
            data-testid={testId.modal.modalClose}
          >
            <Icon name="close" size="mediumlarge" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default Modal;
